import type { RenderTooltipContentData } from '@shopify/polaris-viz';
import type { ICampaignMetricKey } from '../../types';

import { Card, BlockStack, InlineStack, Text } from '@shopify/polaris';
import { getIncrease, IncreasePercentage } from '~/modules/analytics';
import { IVariantType } from '~/modules/campaign/types';
import { isReverseCampaignMetric } from '../../helpers';
import { useTranslation } from 'react-i18next';

export interface AnalyticLineChartTooltipProps {
  data: RenderTooltipContentData;
  metricKey?: ICampaignMetricKey;
}
export const AnalyticLineChartTooltip: React.FC<AnalyticLineChartTooltipProps> = ({
  data,
  metricKey,
}) => {
  const { t } = useTranslation();

  const { activeIndex } = data;

  const dataA = data?.dataSeries.find((item) => item.name === IVariantType.VARIANT_A)?.data;
  const dataB = data?.dataSeries.find((item) => item.name === IVariantType.VARIANT_B)?.data;

  // Extract numeric values for comparison
  const valueA = Number(dataA?.[activeIndex]?.value || 0);
  const valueB = Number(dataB?.[activeIndex]?.value || 0);
  const valuePreviousA = Number(dataA?.[activeIndex - 1]?.value || 0);
  const valuePreviousB = Number(dataB?.[activeIndex - 1]?.value || 0);

  const increaseA = getIncrease(valueA, valuePreviousA);
  const increaseB = getIncrease(valueB, valuePreviousB);

  return (
    <div className="min-w-[175px] w-fit">
      <Card padding={'200'}>
        <BlockStack gap={'100'}>
          <Text as="p" variant="bodySm" fontWeight="semibold">
            {data.formatters?.titleFormatter?.(data.title || '') || data.title}
          </Text>
          <BlockStack gap={'200'}>
            <InlineStack gap={'200'} align="space-between" blockAlign="center">
              <InlineStack gap={'100'} blockAlign="center">
                <div className="bg-variant-a w-[12px] rounded-[10px] h-[2px]"></div>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t('Control')}
                </Text>
              </InlineStack>
              <InlineStack blockAlign="center" gap="100">
                <Text as="span" variant="headingSm">
                  {data.formatters?.valueFormatter?.(valueA)}
                </Text>
                <IncreasePercentage
                  increase={increaseA}
                  isReverse={isReverseCampaignMetric(metricKey)}
                />
              </InlineStack>
            </InlineStack>

            <InlineStack gap={'200'} align="space-between" blockAlign="center">
              <InlineStack gap={'100'} blockAlign="center">
                <div className="bg-variant-b w-[12px] rounded-[10px] h-[2px]"></div>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t('Variant')}
                </Text>
              </InlineStack>
              <InlineStack blockAlign="center" gap="100">
                <Text as="span" variant="headingSm">
                  {data.formatters?.valueFormatter?.(valueB)}
                </Text>
                <IncreasePercentage
                  increase={increaseB}
                  isReverse={isReverseCampaignMetric(metricKey)}
                />
              </InlineStack>
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Card>
    </div>
  );
};
